// Manual task ordering, saved per list (top-level list or a parent's subtasks).
// Stored in localStorage, so the order is per browser rather than synced.

const STORAGE_PREFIX = 'progresstodo.order.';

// Fraction of a row's height at the top and bottom that reorders; the middle nests.
const EDGE_ZONE = 0.25;

export function loadOrder(listKey) {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + listKey);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    return [];
  }
}

export function saveOrder(listKey, ids) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + listKey, JSON.stringify(ids));
  } catch (err) {
    // Storage unavailable (private mode etc.); ordering just won't persist.
  }
}

// Sort tasks by the saved order. Tasks not in the saved order (e.g. new ones) go first,
// newest first, matching where the composer adds them.
export function applyOrder(listKey, tasks) {
  const order = loadOrder(listKey);
  const rank = {};
  order.forEach((id, i) => { rank[id] = i; });
  const unranked = tasks.filter(t => rank[t.id] === undefined)
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  const ranked = tasks.filter(t => rank[t.id] !== undefined)
    .sort((a, b) => rank[a.id] - rank[b.id]);
  return unranked.concat(ranked);
}

// Move `dragId` before or after `targetId` within the currently shown `tasks`,
// then save. Ids of tasks not currently shown (other tabs, search) keep their slots.
export function moveTask(listKey, tasks, dragId, targetId, placeAfter) {
  if (dragId === targetId) return;
  const ids = tasks.map(t => t.id).filter(id => id !== dragId);
  const at = ids.indexOf(targetId);
  if (at === -1) return;
  ids.splice(placeAfter ? at + 1 : at, 0, dragId);

  const shown = {};
  ids.forEach(id => { shown[id] = true; });
  shown[dragId] = true;
  const hidden = loadOrder(listKey).filter(id => !shown[id]);
  saveOrder(listKey, ids.concat(hidden));
}

// Where a drag at `clientY` over a row with bounding `rect` would land.
export function dropPositionAt(rect, clientY) {
  const offset = (clientY - rect.top) / rect.height;
  if (offset < EDGE_ZONE) return 'before';
  if (offset > 1 - EDGE_ZONE) return 'after';
  return 'into';
}

// Short, human-friendly form of a task id for display and referencing.
export function shortId(id) {
  return (id || '').slice(0, 8);
}

// Builds the `sortable` prop TaskRow expects. Drag state lives on `component.state.drag`
// ({ id, overId, position }), so each list only accepts drags that started in it.
// Dropping 'into' a row calls `onNest(draggedTask, targetTask)`.
export function sortableFor(component, listKey, tasks, task, onNest) {
  const drag = component.state.drag;
  const accepts = () => !!component.state.drag;
  return {
    accepts,
    dragging: !!drag && drag.id === task.id,
    dropPosition: drag && drag.overId === task.id && drag.id !== task.id ? drag.position : null,
    onDragStart: (t) => component.setState({ drag: { id: t.id, overId: null, position: null } }),
    onDragOver: (t, position) => {
      const d = component.state.drag;
      if (d && (d.overId !== t.id || d.position !== position)) {
        component.setState({ drag: { ...d, overId: t.id, position } });
      }
    },
    onDrop: () => {
      const d = component.state.drag;
      component.setState({ drag: null });
      if (!d || !d.overId || d.overId === d.id) return;
      if (d.position === 'into') {
        const dragged = tasks.find(t => t.id === d.id);
        const target = tasks.find(t => t.id === d.overId);
        if (dragged && target && onNest) onNest(dragged, target);
      } else {
        moveTask(listKey, tasks, d.id, d.overId, d.position === 'after');
      }
    },
    onDragEnd: () => component.setState({ drag: null }),
  };
}
